import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hideModal } from 'actions/modal';
import Section from 'components/core/Section';
import Text from 'components/core/Text';
import { StoreType } from 'reducers/types';
import imageUrlGenerator from 'utils/imageUrlGenerator';
import DefaultVideoPlayer from 'components/DefaultVideoPlayer';
import Loader from 'components/Loader';
import { Background } from '../styles';
import {
  Content,
  ExerciseModalClose,
  CuesGrid,
  CuePreview,
  CuesWrapper,
  CloseButton,
  SessionLink,
} from './styles';

interface Props {
  modalData?: {
    showSessionLink?: boolean;
  };
}

const ExerciseDetails = ({ modalData }: Props) => {
  const dispatch = useDispatch();
  const exerciseDetails = useSelector((state: StoreType) => state.exercises.exerciseDetails);
  const exerciseDetailsLoading = useSelector(
    (state: StoreType) => state.exercises.exerciseDetailsLoading,
  );
  const programs = useSelector((state: StoreType) => state.programs.all);

  // When opened from the command palette (rather than from a session page),
  // link back to a session containing this exercise — preferring one from a
  // program the user owns, since an exercise can appear in several programs.
  const sessionLink = useMemo(() => {
    if (!modalData?.showSessionLink) {
      return null;
    }

    const candidates = (exerciseDetails?.relation || []).filter(
      (item) => item.session?.id && item.session?.program?.id,
    );

    if (!candidates.length) {
      return null;
    }

    const owned = candidates.find((item) =>
      programs.some((program) => program.id === item.session?.program?.id),
    );
    const target = (owned || candidates[0]).session;

    return {
      href: `/programs/${target?.program?.id}/sessions/${target?.id}`,
      label: `${target?.program?.name} · ${target?.name}`,
    };
  }, [modalData?.showSessionLink, exerciseDetails, programs]);

  const onCloseHandler = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const renderedCues = useMemo(
    () =>
      exerciseDetails?.cueImages && (
        <CuesWrapper pt={6} fillViewport={false}>
          <Text variant="display" mb={5}>
            Form Cues
          </Text>
          <CuesGrid>
            {exerciseDetails?.cueImages?.map((cue) => (
              <div key={cue.id}>
                <CuePreview src={imageUrlGenerator(cue.picture, '480x480', '480x480')} alt="" />
                <Text>{cue.description}</Text>
              </div>
            ))}
          </CuesGrid>
        </CuesWrapper>
      ),
    [exerciseDetails],
  );

  const renderedPlayer = useMemo(
    () => (
      <Section fillViewport={false}>
        {exerciseDetailsLoading && <Loader />}
        {exerciseDetails && exerciseDetails.fullVideo && (
          <DefaultVideoPlayer video={exerciseDetails.fullVideo} />
        )}
      </Section>
    ),
    [exerciseDetails, exerciseDetailsLoading],
  );

  const renderedExerciseData = useMemo(
    () => (
      <Section p={6} pb={8} fillViewport={false}>
        <Text variant="displayX" mb={3}>
          {exerciseDetails?.name}
        </Text>
        {sessionLink && (
          <SessionLink href={sessionLink.href}>From {sessionLink.label} →</SessionLink>
        )}
        <Text mb={6} variant="text">
          {exerciseDetails?.description}
        </Text>
        {renderedCues}
      </Section>
    ),
    [exerciseDetails, renderedCues, sessionLink],
  );

  const renderedContent = useMemo(
    () =>
      exerciseDetailsLoading ? (
        <Section py={10}>
          <Loader />
        </Section>
      ) : (
        <>
          {renderedPlayer}
          {renderedExerciseData}
        </>
      ),
    [exerciseDetailsLoading, renderedExerciseData, renderedPlayer],
  );

  return (
    <>
      <Background onClick={onCloseHandler} />
      <Content key={exerciseDetails?.id}>
        <CloseButton onClick={onCloseHandler}>
          <ExerciseModalClose />
        </CloseButton>
        {renderedContent}
      </Content>
    </>
  );
};

export default ExerciseDetails;
